import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ActionIcon, Button, Group, TextInput, Tooltip } from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
  IconVirus,
  IconX,
} from '@tabler/icons-react';

import {
  GetPathologiesRequestParams,
  PathologiesSortBy,
  Pathology,
} from '@interfaces/pathologies.interface';

import { isApiError } from '@api/index';
import {
  useCountPathologiesQuery,
  useDeletePathologyMutation,
  useGetManyPathologiesQuery,
} from '@api/pathologies.api';

import useLayoutProps from '@hooks/useLayoutProps';
import useStateWithDebounce from '@hooks/useStateWithDebouce';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UpsertPathologyForm, {
  UPSERT_PATHOLOGY_FORM_MODAL_ID,
} from '@components/pathologies/UpsertPathologyForm';

export default function PathologiesList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const canCreatePathologies = useUserPermissionGuard('PATHOLOGIES.CREATE');
  const canEditPathologies = useUserPermissionGuard('PATHOLOGIES.UPDATE');
  const canDeletePathologies = useUserPermissionGuard('PATHOLOGIES.DELETE');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetPathologiesRequestParams>({
    page: +1,
    pageLength: +10,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [nameSearch, setNameSearch, nameSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Pathology>>({
    columnAccessor: 'name',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as PathologiesSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: pathologiesCount = { count: 0 }, isLoading: isLoadingCount } =
    useCountPathologiesQuery({
      nameSearch: nameSearchDebounced,
    });

  const { data = [], isLoading } = useGetManyPathologiesQuery({
    ...filters,
    nameSearch: nameSearchDebounced,
  });

  const [deletePathology, { isLoading: isLoadingDeletePathology }] =
    useDeletePathologyMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleAddPathologyClick = useCallback(() => {
    openModal({
      modalId: UPSERT_PATHOLOGY_FORM_MODAL_ID,
      title: 'Aggiungi patologia',

      children: <UpsertPathologyForm />,
    });
  }, []);

  const handleEditPathologyClick = useCallback((pathology: Pathology) => {
    openModal({
      modalId: UPSERT_PATHOLOGY_FORM_MODAL_ID,
      title: 'Modifica patologia',
      children: <UpsertPathologyForm pathology={pathology} />,
    });
  }, []);

  const handleDeletePathologyClick = useCallback((id: string) => {
    openConfirmModal({
      title: 'Elimina patologia',
      children:
        "Stai per eliminare la patologia. L'operazione non è reversibile.\nContinuare?",

      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deletePathology(id).unwrap();

          showNotification({
            title: 'Patologia eliminata',
            message: 'Patologia eliminata con successo',
          });
        } catch (e) {
          if (isApiError(e)) {
            if (e.data.code === 'P2003') {
              showNotification({
                title: 'Impossibile eliminare',
                message: 'La patologia è in uso da un paziente',
                color: 'red',
                icon: <IconX />,
              });
            } else {
              showNotification({
                title: 'Errore',
                message: e.data.message,
                color: 'red',
                icon: <IconX />,
              });
            }
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconVirus />
        Patologie {!isLoadingCount ? `(${pathologiesCount.count})` : ''}
      </Group>
    ),
    [pathologiesCount.count, isLoadingCount],
  );

  const rightSection = useMemo(
    () =>
      canCreatePathologies ? (
        <Button leftSection={<IconPlus />} onClick={handleAddPathologyClick}>
          Aggiungi patologia
        </Button>
      ) : (
        <></>
      ),

    [handleAddPathologyClick, canCreatePathologies],
  );

  useLayoutProps({
    title,
    rightSection,
  });

  return (
    <>
      <DataTable
        /* General */
        striped
        noRecordsText="Nessuna patologia trovata"
        minHeight={data.length === 0 ? 200 : undefined}
        /* Data */
        fetching={isLoading || isLoadingCount}
        columns={[
          {
            accessor: 'name',
            title: 'Nome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca nome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  nameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={nameSearch}
                onChange={(e) => setNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Group wrap="nowrap" gap="xs">
                {canEditPathologies && (
                  <Tooltip label="Modifica patologia">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => handleEditPathologyClick(record)}
                    >
                      <IconPencil />
                    </ActionIcon>
                  </Tooltip>
                )}
                {canDeletePathologies && (
                  <Tooltip label="Elimina patologia">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      disabled={isLoadingDeletePathology}
                      onClick={() => handleDeletePathologyClick(record.id)}
                    >
                      <IconTrash />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
        records={data}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={pathologiesCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
